<template>
  <div class="App">
    <div class="App__title">GETTING STARTED<br/>WITH MODULAR SYNTH</div>
    <div class="App__synth" ref="synth">
      <div class="App__rack">
        <div class="App__module App__module_color_orange">
          <div class="App__module-title">GUIDE</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot" />
            <div class="App__module-slot">
              <IO title="1" code="guide-1" :active="isActiveIO('guide-1')" ref="guide-1" />
            </div>
            <div class="App__module-slot">
              <IO title="2" code="guide-2" :active="isActiveIO('guide-2')" ref="guide-2" />
            </div>
            <div class="App__module-slot">
              <IO title="3" code="guide-3" :active="isActiveIO('guide-3')" ref="guide-3" />
            </div>
            <div class="App__module-slot">
              <IO title="4" code="guide-4" :active="isActiveIO('guide-4')" ref="guide-4" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="lesson" output color="orange" code="guide-lesson" :active="isActiveIO('guide-lesson')" ref="guide-lesson" />
            </div>
            <div class="App__module-slot" />
            <div class="App__module-slot">
              <IO title="5" code="guide-5" :active="isActiveIO('guide-5')" ref="guide-5" />
            </div>
            <div class="App__module-slot">
              <IO title="6" code="guide-6" :active="isActiveIO('guide-6')" ref="guide-6" />
            </div>
            <div class="App__module-slot">
              <IO title="7" code="guide-7" :active="isActiveIO('guide-7')" ref="guide-7" />
            </div>
            <div class="App__module-slot">
              <IO title="8" code="guide-8" :active="isActiveIO('guide-8')" ref="guide-8" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__lesson">
              {{ lessonNotice }}
            </div>
          </div>
        </div>
      </div>
      <div class="App__rack">
        <div class="App__module App__module_size_3 App__module_color_yellow">
          <div class="App__module-title">MIDI</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot" />
            <div class="App__module-slot">
              <IO title="gate" output color="yellow" code="midi-gate" :active="isActiveIO('midi-gate')" ref="midi-gate" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <div class="App__sequencer">
                <div class="App__sequencer-midi">
                  <div class="App__sequencer-midi-data" v-for="data in lastSignals">
                    <div class="App__sequencer-midi-cmd">{{ data.cmd }}</div>
                    <div class="App__sequencer-midi-note">{{ data.note }}</div>
                  </div>
                </div>
                <div class="App__sequencer-steps">
                  <div :class="sequencerStepClasses(i)" v-for="i in 16" />
                </div>
              </div>
            </div>
            <div class="App__module-slot" />
            <div class="App__module-slot">
              <IO title="v/oct" output color="yellow" code="midi-v-oct" :active="isActiveIO('midi-v-oct')" ref="midi-v-oct" />
            </div>
          </div>
        </div>
        <div class="App__module App__module_size_2 App__module_color_green">
          <div class="App__module-title">AMP</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot">
              <IO title="volume" code="amp-volume" :active="isActiveIO('amp-volume')" ref="amp-volume" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="in" code="amp-in" :active="isActiveIO('amp-in')" ref="amp-in" />
            </div>
            <div class="App__module-slot">
              <IO title="out" output color="green" code="amp-out" :active="isActiveIO('amp-out')" ref="amp-out" />
            </div>
          </div>
        </div>
        <div class="App__module App__module_size_1 App__module_color_red">
          <div class="App__module-title">AUDIO</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="in" code="audio-in" :active="isActiveIO('audio-in')" ref="audio-in" />
            </div>
          </div>
        </div>
      </div>
      <div class="App__rack">
        <div class="App__module App__module_size_3 App__module_color_cyan">
          <div class="App__module-title">OSC</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot" />
            <div class="App__module-slot">
              <IO title="v/oct" code="osc-v-oct" :active="isActiveIO('osc-v-oct')" ref="osc-v-oct" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="saw" output color="cyan" code="osc-saw" :active="isActiveIO('osc-saw')" ref="osc-saw" />
            </div>
            <div class="App__module-slot">
              <IO title="square" output color="cyan" code="osc-square" :active="isActiveIO('osc-square')" ref="osc-square" />
            </div>
            <div class="App__module-slot">
              <IO title="sine" output color="cyan" code="osc-sine" :active="isActiveIO('osc-sine')" ref="osc-sine" />
            </div>
          </div>
        </div>
        <div class="App__module App__module_size_3 App__module_color_coral">
          <div class="App__module-title">FILTER</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="cutoff" :min="0" :gap="100" :max="1200" title="cutoff, hz" :value="options.cutoff" color="coral" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="in" code="filter-in" :active="isActiveIO('filter-in')" ref="filter-in" />
            </div>
            <div class="App__module-slot">
              <IO title="out" output color="coral" code="filter-out" :active="isActiveIO('filter-out')" ref="filter-out" />
            </div>
            <div class="App__module-slot" />
          </div>
        </div>
      </div>
      <div class="App__rack">
        <div class="App__module App__module_color_violet">
          <div class="App__module-title">ADSR</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot" />
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="attack" :mult="1000" :min="0" :gap="0.05" :max="1" title="attack, ms" color="violet" :value="options.attack" />
            </div>
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="decay" :mult="1000" :min="0.01" :gap="0.05" :max="1" title="decay, ms" color="violet" :value="options.decay" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="gate" code="adsr-gate" :active="isActiveIO('adsr-gate')" ref="adsr-gate" />
            </div>
            <div class="App__module-slot">
              <IO title="out" output color="violet" code="adsr-out" :active="isActiveIO('adsr-out')" ref="adsr-out" />
            </div>
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="sustain" :mult="100" :min="0.01" :gap="0.05" :max="100" title="sustain, %" color="violet" :value="options.sustain" />
            </div>
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="release" :mult="1000" :min="0.01" :gap="0.05" :max="1" title="release, ms" color="violet" :value="options.release" />
            </div>
          </div>
        </div>
      </div>
      <div class="App__rack">
        <div class="App__module App__module_color_blue">
          <div class="App__module-title">CHORUS</div>
          <div class="App__module-row">
            <div class="App__module-slot" />
            <div class="App__module-slot" />
            <div class="App__module-slot App__module-slot_size_2">
              <div class="App__scheme App__scheme_bottom_right" />
            </div>
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="delay" title="delay, ms" :mult="1000" :min="0.01" :gap="0.05" :max="1" color="blue" :value="options.delay" />
            </div>
          </div>
          <div class="App__module-row">
            <div class="App__module-slot">
              <IO title="in" color="blue" code="chorus-in" :active="isActiveIO('chorus-in')" ref="chorus-in" />
            </div>
            <div class="App__module-slot">
              <IO title="out" output color="blue" code="chorus-out" :active="isActiveIO('chorus-out')" ref="chorus-out" />
            </div>
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="lfoFreq" title="LFO freq" :min="0" :gap="1" :max="100" color="blue" :value="options.lfoFreq" />
            </div>
            <div class="App__module-slot App__module-slot_size_2">
              <Control code="depth" :mult="100" :min="0" :gap="0.1" :max="1" title="depth, %" color="blue" :value="options.depth" />
            </div>
          </div>
        </div>
      </div>
      <div class="App__patches" ref="patches">
        <template v-for="patch in patches">
          <div class="App__patches-item" v-html="svgPatch(patch)"></div>
        </template>
      </div>
    </div>
    <div class="App__footer">
      <a href="https://www.facebook.com/ianbernatcki" target="_blank">
        <div class="App__footer-avatar">
          <img src="./assets/avatar.png" />
        </div>
        <div class="App__footer-name">
          Ian Bernatcki aka @releu<br />
          Moscow, Russia
        </div>
      </a>
    </div>
    <div class="App__actions" v-if="ioSelected">
      <div class="App__action" @click="removePatch">
        Remove patch
      </div>
    </div>
  </div>
</template>

<script>
import IO from './components/IO.vue'
import Control from './components/Control.vue'
import EventBus from '@/components/EventBus.js';
import Synth from '@/components/Synth.js';

export default {
  name: 'App',
  components: {
    IO,
    Control
  },
  data() {
    const synth = new Synth();

    return {
      patches: [],
      options: {
        cutoff: 1200,
        attack: 0,
        sustain: 1,
        release: 0.2,
        decay: 0.2,
        delay: 0.2,
        depth: 0.5,
        lfoFreq: 4
      },
      ioSelected: null,
      synth: synth,
      lastSignals: [],
      lesson: null,
      step: 1
    }
  },
  mounted() {
    EventBus.$on('io-click', async (code) => {
      if (!this.ioSelected) {
        const patch = this.findPatch(code);
        if (patch) {
          this.patches = this.patches.filter(p => p !== patch)
          const ending = patch[0] == code ? patch[1] : patch[0]
          this.ioSelected = ending
        } else {
          this.ioSelected = code
        }
      } else {
        if (this.ioSelected === code) {
          this.updateSynth();
          this.ioSelected = null;
        } else {
          this.patches = this.patches.filter(p => {
            if (p[0] === code || p[1] === code) {
              return false;
            }
            if (p[0] === this.ioSelected || p[1] === this.ioSelected) {
              return false;
            }
            return true;
          });
          this.patches.push(
            [this.ioSelected, code].sort()
          )
          this.$forceUpdate();
          this.updateSynth();
          this.ioSelected = null;
        }
      }
    });

    let lastEvent = new Date()
    EventBus.$on('step', data => {
      lastEvent = new Date()
      const signals = this.lastSignals.slice(0);
      signals.push(data);
      if ((this.step + 1) > 16) {
        this.step = 1;
      } else {
        this.step += 1;
      }
      this.lastSignals = signals.slice(Math.max(signals.length - 5, 0));
    });

    EventBus.$on('control-change', data => {
      this.options[data.code] = data.value;
      this.updateSynth()
    });
  },
  computed: {
    lessonNotice() {
      const patch = this.findPatch('guide-lesson')
      if (patch) {
        const ending = patch[0] === 'guide-lesson' ? patch[1] : patch[0];
        if (ending.indexOf('guide') !== -1) {
          const lessonNumber = ending.match(/\d/)[0]
          switch (lessonNumber) {
            case '1':
              return 'lesson 1 text...';
            case '2':
              return 'lesson 2 text...';
            case '3':
              return 'lesson 3 text...';
            case '4':
              return 'lesson 4 text...';
            case '5':
              return 'lesson 5 text...';
            case '6':
              return 'lesson 6 text...';
            case '7':
              return 'lesson 7 text...';
            case '8':
              return 'lesson 8 text...';
          }
        }
      }

      return `connect "lesson" with "1" to show first lesson info`;
    }
  },
  methods: {
    isActiveIO(name) {
      return (this.ioSelected === name);
    },
    updateSynth() {
      this.synth.applyOptions(this.patches, this.options)
    },
    findPatch(code) {
      let patch;
      this.patches.forEach(pair => {
        if (pair[0] === code || pair[1] === code) {
          patch = pair;
        }
      })
      return patch;
    },
    svgPatch(patch) {
      const from = patch[0];
      const to = patch[1];
      const patchesNode = this.$refs['patches'];
      const synthRect = this.$refs.synth.getBoundingClientRect()

      const fromNode = this.$refs[from].$el;
      const toNode = this.$refs[to].$el;
      const rootPos = patchesNode.getBoundingClientRect()
      const fromPos = fromNode.getBoundingClientRect()
      const toPos = toNode.getBoundingClientRect()

      const width = rootPos.width;
      const height = rootPos.height;

      const fromX = fromPos.x + fromPos.width / 2 - rootPos.x;
      const fromY = fromPos.y + fromPos.height / 2 - rootPos.y + 2;

      const toX = toPos.x + toPos.width / 2 - rootPos.x;
      const toY = toPos.y + toPos.height / 2 - rootPos.y + 2;

      const weight = 40;

      return `
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
          <path d="M${fromX} ${fromY} C ${fromX} ${fromY + weight}, ${toX + weight} ${toY + weight}, ${toX} ${toY}" stroke="#ffffff" stroke-width="4" fill="transparent"/>
          <circle cx="${fromX}" cy="${fromY + 2}" r="4" fill="#ffffff"/>
          <circle cx="${toX}" cy="${toY + 2}" r="4" fill="#ffffff"/>
        </svg>
      `;
    },
    sequencerStepClasses(i) {
      return {
        'App__sequencer-step': true,
        'App__sequencer-step_active': i === this.step
      }
    },
    removePatch() {
      this.ioSelected = null;
      this.updateSynth();
    }
  }
}
</script>

<style lang="scss">
@font-face {
	font-family: 'custom';
	src: url('./assets/formular.woff2') format('woff2');
	font-weight: normal;
	font-style: normal;
}

body {
  margin: 0;
  background: #303030;
  font-family: custom, sans-serif;
}

.App {
  max-width: 480px;
  margin: 0 auto;

  &__synth {
    position: relative;
  }

  &__rack {
    display: flex;

    & + & {
      margin-top: 1px;
    }
  }

  &__module {
    position: relative;
    flex-grow: 6;
    flex-basis: 0;
    flex-shrink: 0;
    box-sizing: border-box;

    &_size_3 {
      flex-grow: 3;
    }
    &_size_2 {
      flex-grow: 2;
    }
    &_size_1 {
      flex-grow: 1;
    }

    & + & {
      &:after {
        content: '';
        position: absolute;
        left: -1px;
        top: 0;
        bottom: 0;
        width: 1px;
        background: #303030;
      }
    }

    &_color {
      &_orange {
        background: #FA6400;
      }

      &_green {
        background: #89CA73;
      }

      &_red {
        background: #DA4745;
      }

      &_yellow {
        background: #FBC805;
      }

      &_cyan {
        background: #96D0DC;
      }

      &_coral {
        background: #FA7E57;
      }

      &_violet {
        background: #B620E0;
      }

      &_blue {
        background: #1e88e5;
      }
    }

    &-title {
      position: absolute;
      top: 4px;
      left: 4px;
      background: rgba(0, 0, 0, 0.85);
      color: white;
      border-radius: 2px;
      font-size: 12px;
      line-height: 12px;
      padding: 2px 4px;
    }

    &-row {
      display: flex;
    }

    &-slot {
      flex-shrink: 0;
      flex-grow: 1;
      flex-basis: 0;
      min-height: 72px;
      overflow: visible;
      position: relative;

      &_size_2 {
        flex-grow: 2;
      }
    }
  }

  &__sequencer {
    width: 108px;
    height: 108px;
    position: absolute;
    left: 4px;
    bottom: 4px;
    overflow: hidden;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;

    &-midi {
      font-size: 12px;
      line-height: 12px;

      &-data {
        display: flex;
        color: white;
        text-transform: lowercase;

        & + & {
          margin-top: 2px;
        }

        &:nth-last-child(5) {
          opacity: 0.3;
        }
        &:nth-last-child(4) {
          opacity: 0.4;
        }
        &:nth-last-child(3) {
          opacity: 0.5;
        }
        &:nth-last-child(2) {
          opacity: 0.6;
        }
      }

      &-cmd {
        width: 56px;
      }
    }

    &-steps {
      display: flex;
      flex-wrap: wrap;
      margin-top: 8px;
    }

    &-step {
      width: 12px;
      height: 12px;
      border: 1px solid rgba(0, 0, 0, 0.25);
      border-radius: 2px;
      margin-top: 1px;
      box-sizing: border-box;

      & + & {
        margin-left: 1px;
      }

      &_active {
        background: rgba(0, 0, 0, 0.25);
        border-color: transparent;
      }

      &:nth-child(8) + & {
        margin-left: 0;
      }
    }
  }

  &__title {
    font-size: 20px;
    line-height: 24px;
    height: 144px;
    box-sizing: border-box;
    color: white;
    padding: 80px 18px 12px;
  }

  &__lesson {
    background: #C75000;
    padding: 8px 20px 12px 14px;
    // border: 4px solid #FA6400;
    border-radius: 2px;
    margin-left: 4px;
    margin-right: 4px;
    margin-bottom: 4px;
    flex-grow: 1;
    height: 121px;
    font-size: 16px;
    line-height: 20px;
    color: white;
  }

  &__footer {
    font-size: 14px;
    line-height: 16px;
    padding-top: 48px;

    &-patreon {
      text-align: center;
      color: white;

      a {
        color: #00FDF3;
      }
    }

    &-avatar {
      text-align: center;
      line-height: 0;
      font-size: 0;
      margin-top: 24px;

      img {
        width: 32px;
        height: 32px;
        border-radius: 100%;
        box-shadow: 0 4px 8px -2px rgba(0, 0, 0, 0.5);
      }
    }

    > a {
      display: block;
      text-decoration: none;
      padding-top: 8px;
      padding-bottom: 48px;
    }

    &-name {
      text-align: center;
      margin-top: 8px;
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &__patches {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: 200;
    pointer-events: none;

    &-item {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      opacity: 0.9;
    }
  }

  &__scheme {
    border-left: 1px solid rgba(0, 0, 0, 0.25);
    border-top: 1px solid rgba(0, 0, 0, 0.25);
    width: 48px;
    height: 32px;
    box-sizing: border-box;
    position: absolute;
    left: 50%;
    top: 50%;
  }

  &__actions {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 40px;
    display: flex;
    justify-content: center;
    z-index: 200;
    pointer-events: none;
  }

  &__action {
    background: white;
    padding: 11px 24px 13px;
    font-size: 16px;
    line-height: 20px;
    color: black;
    border-radius: 24px;
    box-shadow: 0 4px 8px -4px rgba(0, 0, 8, 0.4);
    pointer-events: all;
    cursor: pointer;
  }
}

@media screen and (min-width: 960px) {
  .App {
    max-width: 960px;

    &__synth {
      width: 960px;
      height: 434px;
    }

    &__rack {
      width: 480px;
      position: absolute;
      margin: 0 !important;
      flex-shrink: 0;

      &:nth-child(1) {
        top: 0;
        left: 0;
        width: 479px;
      }

      &:nth-child(2) {
        top: 290px;
        left: 0;
        width: 479px;
      }

      &:nth-child(3) {
        top: 0;
        left: 480px;
      }

      &:nth-child(4) {
        top: 145px;
        left: 480px;
      }

      &:nth-child(5) {
        top: 290px;
        left: 480px;
      }
    }
  }
}
</style>
